import { Directive, EventEmitter, HostListener, inject, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { IProjectAOI } from "../../../../interfaces/i-project-aoi";
import { ITimelineFilter } from "../../../../interfaces/i-timeline-filter";
import { ViewerConstants } from "../../../../../shared/model/viewer-constants";
import { VIEWER_SERVICE } from "../../../../services/internal-services/viewer-services/inject-base-viewer.service";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../../../shared/model/google-analytics-constants";
import { debounceTime, Subject, Subscription } from "rxjs";
import { ITypeFilter } from "../../../../interfaces/i-type-filter";
import { GoogleAnalyticsService } from "../../../../../shared/services/google-analytics.service";

@Directive()
export abstract class BaseTimelineFilterComponent implements OnInit, OnDestroy {
  viewerService = inject(VIEWER_SERVICE);

  filterExpanded = false;

  _isDummyTimeline: boolean = false;

  visible: IProjectAOI[];

  projects: IProjectAOI[];

  grayskyAvailable: IProjectAOI[] = [];

  grayskyIgnored: IProjectAOI[] = [];

  filters: ITimelineFilter;

  enabled = false;

  mode3D = false;

  @Output() onUpdateProjects = new EventEmitter<any>();

  @Output() onClose = new EventEmitter<any>();

  subscriptions: Subscription[] = [];

  private hitFilterSubject = new Subject<void>();

  private readonly debounceTimeMs = 300;

  @ViewChild("filterPanel") filterPanel: any;

  protected constructor(protected googleAnalyticsService: GoogleAnalyticsService) {
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.TIMELINE_FILTER_OPEN,
      GoogleAnalyticsConstantsCategory.TIMELINE,
      true,
    );
  }

  ngOnInit() {
    //avoid to launch filter multiple times in a short period of time
    this.subscriptions.push(
      this.hitFilterSubject.pipe(debounceTime(this.debounceTimeMs)).subscribe(() => {
        this.updateFilter();
      }),
    );

    this.mode3D = this.viewerService.is3DMode();
    this._isDummyTimeline = this.viewerService.getZoom() < ViewerConstants.COLLECTION_TIMELINE_ZOOM_LEVEL;

    //load data from stored file
    this.filters = this.viewerService.getTimelineFilters();
    this.applyFilters(false);
  }

  changeFilterValue(filter: string, value: string, checked: boolean) {
    this.filters[filter].find((f) => f.value === value).checked = checked;
    this.hitFilterSubject.next();

    const timelineType = this._isDummyTimeline ? "3-15" : "16-21+";
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      checked
        ? GoogleAnalyticsConstantsEvents.TIMELINE_FILTER_TURN_ON_ATTRIBUTE_LEVEL_ + timelineType
        : GoogleAnalyticsConstantsEvents.TIMELINE_FILTER_TURN_OFF_ATTRIBUTE_LEVEL_ + timelineType,
      GoogleAnalyticsConstantsCategory.TIMELINE,
      checked,
    );
  }

  changeVisibilityAoi(filterSelected: ITypeFilter, project: IProjectAOI, checked: boolean) {
    if (checked && !filterSelected.checked) {
      filterSelected.checked = checked;
    }

    this.applyFilters();
    const timelineType = this._isDummyTimeline ? "3-15" : "16-21+";
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      checked
        ? GoogleAnalyticsConstantsEvents.TIMELINE_FILTER_TURN_ON_ATTRIBUTE_LEVEL_ + timelineType
        : GoogleAnalyticsConstantsEvents.TIMELINE_FILTER_TURN_OFF_ATTRIBUTE_LEVEL_ + timelineType,
      GoogleAnalyticsConstantsCategory.TIMELINE,
      checked,
    );
  }

  /**
   * Calculate filter values
   * Update layer visibility with this values
   * Update status for checkboxs
   * Apply changes in visibilities
   */
  updateFilter() {
    this.viewerService.saveTimelineFilters(this.filters);
    this.applyFilters(true);
  }

  /**
   * Check if exist projects match with this type filter after apply filter to projects
   * @param filter
   */
  empty(filter: ITypeFilter): boolean {
    return filter.checked && !!filter.available && filter.visible === 0;
  }

  /**
   * Check if exist projects for this type filter in this view extent
   * @param filter
   */
  available(filter: ITypeFilter): boolean {
    return this.mode3D ? !!filter.available3D : !!filter.available;
  }

  getCheckboxTooltip(filter: ITypeFilter) {
    if (!this.available(filter)) {
      return "Not available in area";
    } else if (this.enabled && this.empty(filter)) {
      return "Correlated attribute checked off";
    }
    return null;
  }

  closePanel() {
    this.onClose.emit(true);
  }

  @HostListener("document:click", ["$event"])
  clickOutside(event) {
    if (!this.insidePanel(event)) {
      this.closePanel();
    }
  }

  insidePanel(event) {
    const panel = this.filterPanel.nativeElement.getBoundingClientRect();
    return (
      event.clientX >= panel.left &&
      event.clientX <= panel.right &&
      event.clientY >= panel.top &&
      event.clientY <= panel.bottom
    );
  }

  ngOnDestroy(): void {
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.TIMELINE_FILTER_CLOSE,
      GoogleAnalyticsConstantsCategory.TIMELINE,
      false,
    );
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  abstract applyFilters(emmit?: boolean): void;
}
